"use client";

import { useState, useRef, useCallback } from "react";
import { Link } from "@/i18n/routing";
import { cn } from "@/lib/utils";
import FallbackImage from "@/components/ui/FallbackImage";
import { formatPrice } from "@/lib/utils";
import { Badge } from "@/components/ui/Badge";
import { useTranslations } from "next-intl";
import { useCart } from "@/context/CartContext";
import { useToast } from "@/context/ToastContext";
import { Heart, ShoppingBag, Check } from "lucide-react";
import { trackEvent } from "@/lib/track";
import type { ProductCardProduct } from "./ProductCard";

export interface ProductListCardProps {
  product: ProductCardProduct;
  className?: string;
}

export function ProductListCard({ product, className }: ProductListCardProps) {
  const {
    name,
    slug,
    price,
    comparePrice,
    image,
    isNew = false,
    inStock = true,
    category = "wigs",
  } = product;

  const t = useTranslations("product");
  const tc = useTranslations("common");
  const { addItem } = useCart();
  const { toast } = useToast();
  const [justAdded, setJustAdded] = useState(false);
  const addTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const href = `/${category}/${slug}`;
  const hasDiscount = comparePrice && comparePrice > price;

  const handleQuickAdd = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      addItem({
        productId: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        image: product.image || undefined,
      });
      toast(`Added to cart: ${product.name}`, "success");
      setJustAdded(true);
      if (addTimerRef.current) clearTimeout(addTimerRef.current);
      addTimerRef.current = setTimeout(() => setJustAdded(false), 1500);
    },
    [addItem, product, toast]
  );

  const handleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Link
      href={href}
      onClick={() => trackEvent("product_card_click", { productId: product.id, productName: name, price, source: "list" })}
      className={cn(
        "group relative flex overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-500 ease-out hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40 focus-visible:ring-offset-2",
        className
      )}
    >
      {/* Image */}
      <div className="relative aspect-square w-36 shrink-0 overflow-hidden bg-brand-cream sm:w-48 md:w-56">
        {image ? (
          <FallbackImage
            src={image}
            alt={`${name} - ${category === "wigs" ? "Premium Wig" : category === "toppers" ? "Hair Topper" : "Accessory"} by Rivky Wigs`}
            fill
            sizes="(max-width: 640px) 144px, (max-width: 768px) 192px, 224px"
            className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
            fallbackClassName="absolute inset-0 bg-gradient-to-br from-brand-gold-light/30 via-brand-cream to-brand-gold/20"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand-gold-light/30 via-brand-cream to-brand-gold/20" />
        )}

        {/* Badges */}
        <div className="absolute start-2 top-2 flex flex-col gap-1">
          {isNew && inStock && <Badge variant="new">{tc("new")}</Badge>}
          {hasDiscount && inStock && <Badge variant="sale">Sale</Badge>}
        </div>

        {/* Sold Out overlay */}
        {!inStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
            <Badge variant="soldOut">{t("soldOut")}</Badge>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex flex-1 flex-col justify-between px-4 py-4 sm:px-6 sm:py-5">
        <div>
          <h3 className="font-display text-sm tracking-tight text-brand-dark transition-colors duration-500 group-hover:text-brand-gold-dark sm:text-base md:text-lg">
            {name}
          </h3>

          <div className="mt-2 flex items-baseline gap-2.5">
            <span
              className={cn(
                "font-display text-base sm:text-lg text-brand-dark",
                !inStock && "text-brand-muted"
              )}
            >
              {formatPrice(price)}
            </span>

            {hasDiscount && (
              <span className="text-xs text-brand-muted/70 line-through">
                {formatPrice(comparePrice)}
              </span>
            )}

            {hasDiscount && (
              <span className="text-xs font-medium text-brand-gold">
                {t("save", { amount: formatPrice(comparePrice - price) })}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-3 flex items-center gap-2">
          {inStock && (
            <button
              onClick={handleQuickAdd}
              className={cn(
                "flex items-center gap-1.5 rounded-sm px-4 py-2 text-[11px] font-medium uppercase tracking-[0.15em] text-white transition-colors duration-300 active:scale-[0.98]",
                justAdded
                  ? "bg-emerald-600"
                  : "bg-brand-dark hover:bg-brand-gold"
              )}
            >
              {justAdded ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  {tc("added")}
                </>
              ) : (
                <>
                  <ShoppingBag className="h-3.5 w-3.5" />
                  {tc("quickAdd")}
                </>
              )}
            </button>
          )}

          <button
            onClick={handleWishlist}
            aria-label={tc("wishlist")}
            className="flex h-8 w-8 items-center justify-center rounded-full text-brand-muted transition-colors hover:bg-brand-gold/10 hover:text-brand-gold"
          >
            <Heart className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Link>
  );
}
